import type { JsonValue } from 'type-fest';

import { isAbortErrorLike } from '@frontend/controllers';
import { wait } from '@frontend/utils';

import { FetchError } from '../../fetchError';
import type { ResponsePayload, RetryOptions } from '../../types';
import type { HasFetchProgress, ResponseOptions } from '../types';
import {
  getAccessToken,
  getErrorMessage,
  getNumberOfAttemptsRemaining,
  getPayload,
  getRetryDelay,
  getShouldRetry,
} from '../util';
import { getRequestMethod } from './getRequestMethod';

type RequestWait = Promise<void>;

// A Map of the URL's host to a Promise that resolves after waiting.
const requestWaitByHost = new Map<string, RequestWait>();

const waitForRequestWait = async ({
  requestWait,
  host,
}: {
  requestWait?: RequestWait;
  host: string;
}): Promise<boolean> => {
  await requestWait;

  // Cleanup if same requestWait after waiting.
  if (requestWaitByHost.get(host) === requestWait) {
    requestWaitByHost.delete(host);
    return true;
  }

  return false;
};

const requestRetry = async <T extends ResponsePayload>({
  url,
  extendedOptions,
  numberOfFailedAttempts,
  response,
  responseOptions,
}: MakeRequestParams & {
  response: Response;
}): Promise<T> => {
  const { fetchProgress, retryOptions, ...requestInit } = extendedOptions;

  const numberOfAttemptsRemaining = getNumberOfAttemptsRemaining({
    retryOptions,
    numberOfFailedAttempts,
  });

  const retryDelay = await getRetryDelay({
    retryOptions,
    numberOfFailedAttempts,
    response,
  });

  const delaySeconds = retryDelay / 1000;

  const method = getRequestMethod(requestInit);
  console.warn(
    `Warning ${method} ${url}: (Status: ${
      response.status
    }). Retrying in ${delaySeconds} seconds.${
      typeof numberOfAttemptsRemaining === 'number'
        ? ` Attempts remaining: ${numberOfAttemptsRemaining}`
        : ''
    }`,
  );

  const requestWait = wait(retryDelay);
  const host = new URL(url).host;
  requestWaitByHost.set(host, requestWait);

  await waitForRequestWait({ requestWait, host });
  console.warn(`Finished waiting. Proceeding with retrying ${method} ${url}`);

  // Update Authorization token in headers (if defined in headers and there is a
  // token)
  const token = getAccessToken();
  const headers = requestInit?.headers;
  if (headers && token) {
    const authorization = `Token ${token}`;
    if (headers instanceof Headers) {
      if (headers.has('Authorization')) {
        headers.set('Authorization', authorization);
      }
    } else if (Array.isArray(headers)) {
      const authorizationPair = headers.find((h) => (h[0] = 'Authorization'));
      if (authorizationPair) {
        authorizationPair[1] = authorization;
      }
    } else if (headers.Authorization) {
      headers.Authorization = authorization;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return makeRequest<T>({
    url,
    extendedOptions,
    numberOfFailedAttempts,
    responseOptions,
  });
};

type MakeRequestParams = {
  url: string;
  extendedOptions: RequestInit &
    Partial<HasFetchProgress> & { retryOptions: RetryOptions };
  numberOfFailedAttempts: number;
  responseOptions?: ResponseOptions;
};

const makeRequest = async <T extends ResponsePayload>({
  url,
  extendedOptions,
  numberOfFailedAttempts,
  responseOptions,
}: MakeRequestParams): Promise<T> => {
  const { fetchProgress, retryOptions, ...requestInit } = extendedOptions;

  const method = getRequestMethod(requestInit);
  // Note: res.status is also 0 if not sent by browser despite fetch() being called.
  let status = 0;
  let currentNumberOfFailedAttempts = numberOfFailedAttempts;

  // If this is the first attempt at making this request, and another request
  // against this host is waiting, wait for requestWait before making the
  // request.
  if (currentNumberOfFailedAttempts === 0) {
    const host = new URL(url).host;

    if (requestWaitByHost.has(host)) {
      console.warn(
        `Waiting for ${method} ${url}: Because of previous failed/throttled request for this host`,
      );
      const requestWait = requestWaitByHost.get(host);
      await waitForRequestWait({ requestWait, host });
      console.warn(`Finished waiting. Proceeding with ${method} ${url}`);
    }
  }

  try {
    fetchProgress?.startRequest();
    let response = await fetch(url, requestInit);
    status = response.status;
    response = fetchProgress?.startResponse(response) ?? response;

    // If response.ok is falsy, this attempt has failed, so we update the
    // failure counter.
    if (!response.ok) {
      currentNumberOfFailedAttempts = numberOfFailedAttempts + 1;
    }

    if (
      getShouldRetry({
        response,
        retryOptions,
        numberOfFailedAttempts: currentNumberOfFailedAttempts,
      })
    ) {
      return requestRetry<T>({
        url,
        extendedOptions,
        response,
        numberOfFailedAttempts: currentNumberOfFailedAttempts,
        responseOptions,
      });
    }

    if (response.ok) {
      return getPayload<T>(response, responseOptions);
    }

    // TODO: At some point we may want to refactor this so that the redirect
    // behavior is customizable via the calling context and/or API context.
    const currentLocation = window.location.pathname;
    if (status === 401 && !currentLocation.includes('login')) {
      console.error(
        `Error ${method} ${url}: (Status: 401). Redirecting to login`,
      );
      const initialPage = currentLocation + window.location.search;
      window.location.replace(
        `${window.location.origin}/login?next=${initialPage}`,
      );
    } else if (status === 402) {
      console.error(
        `Error ${method} ${url}: (Status: 402). Redirecting to login`,
      );
      window.location.replace(`${window.location.origin}/payment-required`);
    }

    const body: JsonValue = await getPayload<JsonValue>(response).catch((e) =>
      Promise.resolve({
        statusText: response.statusText,
        message: getErrorMessage(e),
      }),
    );

    throw new FetchError(status, body);
  } catch (error) {
    fetchProgress?.requestError(error);
    const method = getRequestMethod(requestInit);
    if (isAbortErrorLike(error)) {
      console.warn(`Aborted ${method} ${url}:`);
      throw error;
    }
    if (error instanceof FetchError) {
      console.error(
        `Error ${method} ${url}: (Status: ${status})`,
        getErrorMessage(error),
      );
      throw error;
    }
    console.error(
      `Error: Request not sent by browser: ${method} ${url}: (Status: ${status})`,
      getErrorMessage(error),
    );
    throw new FetchError(0, null);
  }
};

/**
 * Function to handle making requests to Paperless Parts' backend.
 *
 * This function handles waiting when previous requests have been throttled,
 * as well as retrying failed requests, and other common requirements.
 *
 * Note: The request method and URL are included in the logging for this
 * function to help with debugging from FullStory. (Otherwise, we just have the
 * stack trace in the minified code, which is hard to follow.)
 *
 * @param url The URL to which to send the request.
 * @param extendedOptions Options for the request. This is a RequestInit object,
 *   plus a couple of custom properties.
 * @returns Returns a promise of a RequestPayload, which is the body of the
 *   response with some processing applied to it (e.x. deserializing JSON).
 */
export const request = async <T extends ResponsePayload>(
  url: MakeRequestParams['url'],
  extendedOptions: MakeRequestParams['extendedOptions'],
  responseOptions?: ResponseOptions,
): Promise<T> =>
  makeRequest({
    url,
    extendedOptions,
    numberOfFailedAttempts: 0,
    responseOptions,
  });
